import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { get } from '../helper/APIClient';
import FullPageLoader from '../helper/FullPageLoader';
import { Routes } from '../helper/Routes';

export interface Branding {
  idBrand: string;
  idBrandStr?: string;
  brandName: string;
  isPrimary: boolean;
  isPublic?: boolean;
  layout?: number;
  frontColour: string;
  accentColour?: string;
  buttonColour?: string;
  topMenuBackground: string;
  topMenuFontColour: string;
  pageBackground: string;
  pageHeaderBackground?: string;
  pageHeaderLine?: string;
  pageBoxBorder: string;
  pageBoxFill: string;
  pageBoxFillAlt?: string;
  pageHeaderFontColour: string;
  pageStrapFontColour: string;
  textMutedColour?: string;
  folderSvg: number;
  footerBackground: string;
  footerGraphic?: string;
  createdAt: string;
  updatedAt: string;
}

interface BrandingContextType {
  branding: Branding | null;
  loading: boolean;
  error: string | null;
  refreshBranding: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

interface BrandingProviderProps {
  children: ReactNode;
}

const BRANDING_CACHE_KEY = 'familyBranding';
let brandingCache: Branding | null = null;
let brandingRequest: Promise<Branding> | null = null;

const readCachedBranding = (): Branding | null => {
  if (brandingCache) {
    return brandingCache;
  }

  try {
    const rawBranding = sessionStorage.getItem(BRANDING_CACHE_KEY);
    if (!rawBranding) {
      return null;
    }

    const parsedBranding = JSON.parse(rawBranding) as Branding;
    if (!parsedBranding?.idBrand) {
      return null;
    }

    brandingCache = parsedBranding;
    return parsedBranding;
  } catch {
    sessionStorage.removeItem(BRANDING_CACHE_KEY);
    return null;
  }
};

const writeCachedBranding = (brandingData: Branding) => {
  brandingCache = brandingData;
  sessionStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(brandingData));
};

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  const [branding, setBranding] = useState<Branding | null>(() => readCachedBranding());
  const [loading, setLoading] = useState<boolean>(() => !readCachedBranding());
  const [error, setError] = useState<string | null>(null);
  const hasLoadedBranding = useRef(!!readCachedBranding());
  const fetchBranding = async (options?: { silent?: boolean; force?: boolean }) => {
    const silent = options?.silent ?? false;
    const force = options?.force ?? false;

    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      const cachedBranding = !force ? readCachedBranding() : null;
      if (cachedBranding) {
        setBranding(cachedBranding);
        applyBrandingStyles(cachedBranding);
        return;
      }

      if (!brandingRequest || force) {
        brandingRequest = get(Routes.BRANDING_FAMILY)
          .then((response) => {
            if (!response || !response.idBrand) {
              throw new Error('No primary branding found');
            }

            writeCachedBranding(response);
            return response as Branding;
          })
          .finally(() => {
            brandingRequest = null;
          });
      }

      const response = await brandingRequest;
      setBranding(response);
      applyBrandingStyles(response);
      hasLoadedBranding.current = true;
    } catch (err) {
      console.error('Failed to fetch branding:', err);
      setError('Failed to load branding');
      // Apply default branding if fetch fails
      applyDefaultBranding();
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const applyBrandingStyles = (brandingData: Branding) => {
    const root = document.documentElement;
    
    // Apply CSS custom properties with correct mappings:
    // 1. Logo file (handled separately in components)
    // 2. front_colour - The colour used on the font (primary text/brand color)
    root.style.setProperty('--brand-font-colour', brandingData.frontColour);
    
    // 2b. accent_colour - A second accent tone, distinct from front_colour
    root.style.setProperty('--brand-accent-colour', brandingData.accentColour || brandingData.frontColour);

    // 2c. button_colour - Primary call-to-action button colour, distinct from front_colour
    root.style.setProperty('--brand-button-colour', brandingData.buttonColour || brandingData.frontColour);

    // 3. top_menu_background - The colour of the top menu bar
    root.style.setProperty('--brand-top-menu-bg', brandingData.topMenuBackground);
    
    // 4. top_menu_font_colour - The colour of the font on the top menu bar
    root.style.setProperty('--brand-top-menu-font', brandingData.topMenuFontColour);
    
    // 5. page_background - The background colour of the page
    root.style.setProperty('--brand-page-bg', brandingData.pageBackground);

    // 5b. page_header_background - The background colour of the page title/header band
    root.style.setProperty(
      '--brand-page-header-bg',
      brandingData.pageHeaderBackground || brandingData.pageBoxFill || brandingData.pageBackground
    );

    // 5c. page_header_line - The divider colour below the page title/header band
    root.style.setProperty(
      '--brand-page-header-line',
      brandingData.pageHeaderLine || brandingData.pageBoxBorder || '#e5e7eb'
    );
    
    // 6. page_box_border - The border colour for boxes on the page
    root.style.setProperty('--brand-page-box-border', brandingData.pageBoxBorder);
    
    // 7. page_box_fill - What colour the boxes are filled with
    root.style.setProperty('--brand-page-box-fill', brandingData.pageBoxFill);

    // 7b. page_box_fill_alt - Soft/secondary card fill, distinct from page_box_fill
    root.style.setProperty('--brand-page-box-fill-alt', brandingData.pageBoxFillAlt || brandingData.pageBackground);

    // 8. page_header_font_colour - The colour of the page header
    root.style.setProperty('--brand-page-header-font', brandingData.pageHeaderFontColour);
    
    // 9. page_strap_font_colour - The colour of the strap line
    root.style.setProperty('--brand-page-strap-font', brandingData.pageStrapFontColour);

    // 9b. text_muted_colour - A deeper muted-text tone, distinct from page_strap_font_colour
    root.style.setProperty('--brand-text-muted', brandingData.textMutedColour || brandingData.pageStrapFontColour);

    // 10. foldersvg - The folder type (1 or 2)
    root.style.setProperty('--brand-folder-svg', brandingData.folderSvg.toString());
    
    // 11. footer_background - The background colour of the footer
    root.style.setProperty('--brand-footer-bg', brandingData.footerBackground);
    
    // 12. footer_graphic - The footer graphic (stored for components to use)
    if (brandingData.footerGraphic) {
      root.style.setProperty('--brand-footer-graphic', brandingData.footerGraphic);
    } else {
      root.style.removeProperty('--brand-footer-graphic');
    }
  };

  const applyDefaultBranding = () => {
    const root = document.documentElement;

    // Default values — Aperture Mesh identity (POC/src/styles/tokens.css),
    // reconciled with AuthPortalContext's applyBrandingStyles(null) fallback
    // so authenticated and pre-auth pages degrade to the same look.
    root.style.setProperty('--brand-font-colour', '#4F46E5'); // --fs-indigo
    root.style.setProperty('--brand-accent-colour', '#B97A5D'); // --fs-clay
    root.style.setProperty('--brand-button-colour', '#1A1410'); // --fs-ink
    root.style.setProperty('--brand-top-menu-bg', '#FAF6EE'); // --fs-cream
    root.style.setProperty('--brand-top-menu-font', '#1A1410'); // --fs-ink
    root.style.setProperty('--brand-page-bg', '#FAF6EE'); // --fs-cream
    root.style.setProperty('--brand-page-header-bg', '#FFFFFF'); // --fs-white
    root.style.setProperty('--brand-page-header-line', '#E5DDD0'); // --fs-line
    root.style.setProperty('--brand-page-box-border', '#D8CDBC'); // --fs-line-strong
    root.style.setProperty('--brand-page-box-fill', '#FFFFFF'); // --fs-white
    root.style.setProperty('--brand-page-box-fill-alt', '#F0E9DD'); // --fs-biscuit
    root.style.setProperty('--brand-page-header-font', '#1A1410'); // --fs-ink
    root.style.setProperty('--brand-page-strap-font', '#4A433D'); // --fs-ink-70
    root.style.setProperty('--brand-text-muted', '#6F665E'); // --fs-ink-50
    root.style.setProperty('--brand-folder-svg', '1'); // Default folder type
    root.style.setProperty('--brand-footer-bg', '#F0E9DD'); // --fs-biscuit
  };

  const refreshBranding = async () => {
    await fetchBranding({ silent: true, force: true });
  };

  useEffect(() => {
    if (branding) {
      applyBrandingStyles(branding);
      hasLoadedBranding.current = true;
      setLoading(false);
      void fetchBranding({ silent: true, force: true });
      return;
    }

    fetchBranding();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasLoadedBranding.current) return;
    fetchBranding({ silent: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const handleWindowFocus = () => {
      if (!hasLoadedBranding.current) return;
      fetchBranding({ silent: true });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!hasLoadedBranding.current) return;
      fetchBranding({ silent: true });
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, loading, error, refreshBranding }}>
      {loading ? <FullPageLoader /> : children}
    </BrandingContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
